/**
 * KS2-02 — списковые поля документа как обычный редактируемый текст.
 *
 * Поля вида `kind:'list'` (Technical/Behavioral/Culture fit у interview questions,
 * Responsibilities и Requirements у JD) хранятся массивом строк. В Этапе 4 они
 * остались простыми `<textarea>` — по правилу «один пункт на строку, редактор
 * список развалит». После перевода генерации на HTML это правило обернулось
 * дефектом: модель кладёт в пункт `<p><b>How do you stay current…`, и рекрутёр
 * видит теги. Замечание клиента 2026-07-31 — «юзер везде видит единообразный
 * интерфейс, а не теги».
 *
 * Решение: массив ↔ маркированный список редактора. Пункт становится `<li>`, то
 * есть граница пунктов выражена структурой документа, а не переводом строки —
 * поэтому форматирование ВНУТРИ пункта наконец можно и хранить, и показывать.
 *
 * Обе функции чистые и лежат отдельно от компонента намеренно: именно здесь
 * список молча теряет пункты, и проверять это надо функцией, а не через
 * смонтированный ProseMirror.
 */
import { normalizeRichText, renderFreeMarkdown } from '@eer/shared';

/** Блоки, которые считаются самостоятельным пунктом, если список развалили. */
const BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'BLOCKQUOTE', 'DIV']);

/**
 * Массив пунктов → HTML маркированного списка для редактора.
 *
 * Каждый пункт проходит через `normalizeRichText`: значения, написанные до
 * перехода на HTML, это markdown, и отдать редактору `**bold**` как HTML значило
 * бы показать звёздочки, а после сохранения заморозить их в тексте навсегда.
 *
 * Пустой массив даёт пустую строку, а НЕ `<ul></ul>`: пустой список — это документ,
 * который ProseMirror показывает как пустую строку с маркером, и пользователь
 * получает пункт, которого не создавал.
 */
export function listItemsToEditorHtml(value: unknown): string {
  const items = toStringItems(value);
  if (items.length === 0) return '';
  const li = items
    .map((item) => normalizeRichText(item, renderFreeMarkdown))
    .filter((html) => html.trim().length > 0)
    .map((html) => `<li>${html}</li>`)
    .join('');
  return li ? `<ul>${li}</ul>` : '';
}

/**
 * HTML редактора → массив пунктов.
 *
 * Читается ЛЮБОЙ документ, а не только идеальный `<ul><li>`: пользователь может
 * выйти из списка, нажать Enter дважды или вставить текст абзацами. Тогда пунктом
 * становится каждый блок верхнего уровня. Молчаливая потеря пунктов здесь — это
 * потеря вопросов из готового документа, поэтому разбор нарочно всеядный.
 *
 * Один абзац внутри `<li>` разворачивается: `<li><p>x</p></li>` → `x`. Так пункт
 * остаётся строчным HTML — той же формы, что и раньше хранились простые строки,
 * и все существующие читатели (режим просмотра, `.docx`-мост, письма) не меняются.
 */
export function editorHtmlToListItems(html: unknown): string[] {
  const text = String(html ?? '').trim();
  if (!text) return [];
  const doc = new DOMParser().parseFromString(`<body>${text}</body>`, 'text/html');
  const body = doc.body;

  const out: string[] = [];
  const pushNode = (el: Element): void => {
    const inner = unwrapSingleParagraph(el);
    if (inner.trim().length > 0) out.push(inner);
  };

  const listItems = body.querySelectorAll('li');
  if (listItems.length > 0) {
    // Вложенный список остаётся ВНУТРИ своего пункта: вытащить его отдельным
    // пунктом значило бы потерять иерархию, которую пользователь построил.
    for (const li of listItems) {
      if (li.parentElement?.closest('li')) continue;
      pushNode(li);
    }
    return out;
  }

  for (const child of body.children) {
    if (BLOCK_TAGS.has(child.tagName)) pushNode(child);
  }
  // Совсем без блоков (голый текст) — это один пункт.
  if (out.length === 0 && body.textContent?.trim()) out.push(text);
  return out;
}

/** Значение поля → массив строк (объекты-варианты сводятся к своему телу). */
function toStringItems(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((x) =>
      typeof x === 'string' ? x : ((x as { body?: string })?.body ?? JSON.stringify(x)),
    );
  }
  if (typeof value === 'string' && value.trim()) return value.split('\n');
  return [];
}

/** `<li><p>x</p></li>` → `x`; всё остальное — как есть. */
function unwrapSingleParagraph(el: Element): string {
  const kids = [...el.childNodes].filter(
    (n) => n.nodeType !== 3 /* text */ || (n.textContent ?? '').trim().length > 0,
  );
  if (kids.length === 1 && kids[0]!.nodeType === 1 && (kids[0] as Element).tagName === 'P') {
    return (kids[0] as Element).innerHTML.trim();
  }
  return el.innerHTML.trim();
}
